import { useQuery } from '@tanstack/react-query';
import { ethers } from 'ethers';
import { useContractQuery } from './useContractQuery';
import { useReadContracts } from './useContracts';
import { SLOT_HASHES, SLOT_NAMES, type SlotName } from '../lib/slots';

/* -------------------------------------------------------------- *
 *   Counts and aggregates
 * -------------------------------------------------------------- */

export function useProposalCount() {
  return useContractQuery<bigint>(['governance', 'proposalCount'], { refetchInterval: 20_000 });
}

/** Last snapshot id taken by governance (separate from crowdsale's). */
export function useGovernanceLastSnapshotId() {
  return useContractQuery<bigint>(['governance', '_lastSnapshotId']);
}

/* -------------------------------------------------------------- *
 *   Proposal scalar fields
 *
 *   The auto-generated getter for proposalsMapping(uint256) returns
 *   only the scalar struct fields — bool[] / bytes32[] / address[]
 *   submitted via submitProposal() are stripped. Without on-chain
 *   events (none defined) the array fields are unreadable by the
 *   frontend. Phase 2.5 (Supabase) will mirror submissions so the
 *   UI can render proposal subjects + targets.
 * -------------------------------------------------------------- */

export type ProposalRow = {
  snapshotId: bigint;
  voteCount: bigint;
  deadLine: bigint;
  threshold: bigint;
  isSuccesfull: boolean;
};

export function useProposal(id: number | bigint | null | undefined) {
  return useContractQuery<ProposalRow>(
    ['governance', 'proposalsMapping', BigInt(id ?? 0)],
    { enabled: id !== null && id !== undefined }
  );
}

/** Returns the address registered at `activeContracts[idx]`. */
export function useActiveContractAt(idx: number | bigint) {
  return useContractQuery<string>(['governance', 'activeContracts', BigInt(idx)]);
}

/* -------------------------------------------------------------- *
 *   Slot registry — read EternalStorage for the live mapping
 * -------------------------------------------------------------- */

/**
 * Returns the contract address currently registered at the given slot.
 * The slot is the keccak256 of the human-readable slot name
 * ("Token", "Token Management", "Auditing Pool", "Governance").
 */
export function useSlotAddress(slotHash: string | undefined) {
  return useContractQuery<string>(
    ['storage', 'getWritterBySlot', slotHash ?? '0x' + '00'.repeat(32)],
    { enabled: !!slotHash, staleTime: 30_000 }
  );
}

/** Convenience — read the live address for one of the named slots. */
export function useNamedSlotAddress(name: SlotName) {
  return useSlotAddress(SLOT_HASHES[name]);
}

/* -------------------------------------------------------------- *
 *   Proposal mirror — event-scan based (#5)
 *
 *   The OpenGovernance contract emits ProposalSubmitted(proposalId,
 *   proposer, snapshotId, deadLine, threshold) but the proposal's
 *   array fields (wallet[], subject[], isIssue[]) are NOT in the
 *   event — they're only in the transaction calldata. To render
 *   "what is this proposal about" we have to:
 *      1. queryFilter past ProposalSubmitted events
 *      2. for each, getTransaction(log.transactionHash) to recover
 *         the original submitProposal() calldata
 *      3. decode the input data using the function fragment
 *
 *   This is the mockup of what a Supabase indexer would otherwise do.
 *   Cost: ~2 RPC calls per past proposal (queryFilter + getTransaction).
 *   For a Sepolia testnet with single-digit proposals this is cheap
 *   enough. For mainnet scale we'd replace this with an indexer.
 * -------------------------------------------------------------- */

/** A proposal as seen by the mirror — combines the on-chain event
 *  with the function calldata to recover the array fields. */
export type ProposalMirrorRow = {
  proposalId: number;
  proposer: string;
  wallet: string[];        // newContract targets
  subject: string[];       // bytes32 slot hashes (or topic hashes for issues)
  isIssue: boolean[];      // per-target: true = "issue", false = slot-bind
  blockNumber: number;
  txHash: string;
};

/** Try to label a bytes32 subject hash as one of the known slot names.
 *  Returns null if no match. Useful for rendering proposals as
 *  "rebind Governance → 0x…" instead of "method 0xabc…". */
export function slotNameForHash(hash: string | null | undefined): SlotName | null {
  if (!hash) return null;
  const normalized = hash.toLowerCase();
  for (const name of SLOT_NAMES) {
    if (SLOT_HASHES[name].toLowerCase() === normalized) return name;
  }
  return null;
}

/**
 * Walks past ProposalSubmitted events on OpenGovernance and decodes each
 * submitProposal() transaction to recover the array fields. Returns a
 * map keyed by proposalId so consumers can lookup quickly.
 *
 * fromBlock defaults to a generous lookback (last ~200k blocks) which
 * covers Sepolia activity since v3 was deployed without hitting most
 * public-RPC range limits. Tune via the argument if needed.
 */
export function useProposalsMirror(opts?: { fromBlock?: number }) {
  const contracts = useReadContracts();
  const fromBlock = opts?.fromBlock ?? -200_000; // negative = relative to head
  return useQuery<Record<number, ProposalMirrorRow>, Error>({
    queryKey: ['metha', 'governance', 'proposalMirror', fromBlock] as const,
    queryFn: async () => {
      const gov = contracts.governance;
      const provider = gov.runner?.provider as ethers.Provider | undefined;
      if (!provider) throw new Error('No provider on governance contract');

      // Resolve the absolute fromBlock if a relative offset was passed.
      let resolvedFrom = fromBlock;
      if (resolvedFrom < 0) {
        const head = await provider.getBlockNumber();
        resolvedFrom = Math.max(0, head + resolvedFrom);
      }

      // 1. queryFilter ProposalSubmitted events.
      const filter = gov.filters.ProposalSubmitted();
      const logs = await gov.queryFilter(filter, resolvedFrom, 'latest');

      // 2. Build a function fragment for submitProposal so we can decode tx data.
      const iface = new ethers.Interface([
        'function submitProposal(address[] wallet, bytes32[] subject, bool[] isIssue)',
      ]);

      // 3. For each log, fetch the originating tx and decode calldata.
      //    Use Promise.all to parallelise; small N expected on Sepolia.
      const rows = await Promise.all(logs.map(async (log) => {
        const parsedEvent = gov.interface.parseLog({ topics: [...log.topics], data: log.data });
        const proposalId = Number(parsedEvent?.args?.proposalId ?? 0n);
        const proposer = String(parsedEvent?.args?.proposer ?? '0x' + '0'.repeat(40));

        let wallet: string[] = [];
        let subject: string[] = [];
        let isIssue: boolean[] = [];
        try {
          const tx = await provider.getTransaction(log.transactionHash);
          if (tx?.data) {
            // Some early proposals may have used a wrapper / proxy. Try a
            // direct decode first; on failure fall back to empty arrays so
            // the row still renders with the scalar event data.
            const decoded = iface.decodeFunctionData('submitProposal', tx.data);
            wallet = Array.from(decoded[0] as string[]);
            subject = Array.from(decoded[1] as string[]);
            isIssue = Array.from(decoded[2] as boolean[]);
          }
        } catch {
          // Decode failed (proxy call, different ABI version, etc.) — keep
          // arrays empty; consumer will render "calldata unreadable".
        }

        return {
          proposalId,
          proposer,
          wallet,
          subject,
          isIssue,
          blockNumber: log.blockNumber,
          txHash: log.transactionHash,
        } satisfies ProposalMirrorRow;
      }));

      // 4. Index by proposalId so consumers can lookup in O(1).
      const map: Record<number, ProposalMirrorRow> = {};
      for (const r of rows) map[r.proposalId] = r;
      return map;
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
}
